/// <amd-module name="Wit/Common/BacklogContentService/BacklogContentDataProviderTypes" />
import { IObservableValue } from "VSS/Core/Observable";
import { RollupProgressBarData } from "Wit/AgileAnalytics/WorkItemRollupProvider";
import { FieldType } from "Wit/Common/Generated/Constants";
import { RollupCalculationSettings } from "./RollupHelpers";

// these interfaces map directly to the payload data from the data provider:
// Microsoft.TeamFoundation.Server.WebAccess.Agile.Plugins.BacklogsHub.DataProviders.BacklogsHubContentDataProvider

// due to issues server side, they can not be generated by genclient without *serious* work
// DO NOT MODIFY unless you know what you are doing

export type RawRowData = string | number | Date | null | IObservableValue<RollupProgressBarData | undefined>;

export interface IBacklogData extends IHubData {
    activeWorkItemTypes: string[];
    backlogPayload: IBacklogPayload;
}

export interface IBacklogSettingsData {
    teamName: string;
    teamId: string;
    backlogLevelName: string;
    automationRulesStates: { [key: string]: boolean } | undefined;
}

export interface ICommonBacklogData {
    backlogLevelId: string;
    backlogQueryResults: IBacklogQueryResults | undefined;
    backlogWiql: string;
    backlogWorkItemTypeNames: string[];
    initialBacklogFilterJson: string;
    columnPreferences?: IColumnPreference[];
}

export interface ISprintsBacklogData extends IHubData {
    activeWorkItemTypes: string[];
    backlogContext: IBacklogContext;
    backlogQueryResults: IBacklogQueryResults;
    columnPreferences: IColumnPreference[];
}

export interface IHubData {
    initialBacklogFilterJson: string;
}

export interface IBacklogPayload {
    backlogContext: IBacklogContext;
    backlogContextWorkItemTypeNames: string[];
    columnPreferences: IColumnPreference[];
    cumulativeFlowDiagramSettings: ICumulativeFlowDiagramSettings;
    forecastSettings: IForecastSettings;
    isRequirementBacklog: boolean;
    isRootBacklog: boolean;
    queryResults: IBacklogQueryResults;
    automationRulesStates: { [key: string]: boolean } | undefined;
}

export interface ICumulativeFlowDiagramSettings {
    backlogLevelId: string;
}

export interface IBacklogContext {
    levelName: string;
    team: ITeam;
}

export enum BacklogQueryResultType {
    Default = 0,
    OneHop = 1,
    Flat = 2
}

export interface IBacklogQueryResults {
    // these are definitions for the visible columns
    columns: IBacklogColumn[];

    // configured columns and all items that match the backlog settings
    payload: IBacklogQueryResultsPayload;

    backlogQueryResultType: BacklogQueryResultType;

    productBacklogGridOptions: IBacklogGridOptions;

    realParentIds: number[];

    // maps child to parent (0 means no parent)
    sourceIds: number[];

    // all items in the backlog (unsure on limit, may be capped at 950)
    // -1 means it's an "unparented items" folder row, and there should only be one
    targetIds: number[];

    // query to generate these results
    wiql: string;
}

export interface IBacklogColumn {
    canMove: boolean;
    canSortBy: boolean;
    fieldId?: number;
    fieldType?: FieldType;
    fixed: boolean;
    id?: number;
    isIdentity?: boolean;
    name: string;
    text: string;
    width: number;
    notAField?: boolean;
    rollup?: boolean;
    rollupCalculation?: RollupCalculationSettings;
    columnIndex?: number;
}

export interface IColumnPreference {
    fieldName: string;
    columnWidth: number;
    notAField: boolean;
    rollup: boolean;
    rollupCalculation: RollupCalculationSettings;
    columnIndex?: number;
}

export interface IBacklogGridOptions {
    enableForecast: boolean;
}

export interface IBacklogQueryResultsPayload {
    // these are all the configured columns for work items
    columns: string[];

    // these are the values of the work items
    rows: RawRowData[][];
}

/** Interface for forecasting data, to be kept in sync with EffortDataViewModel.cs */
export interface IEffortData {
    ids: number[];
    efforts: number[];
    effortFieldName: string;
}

export interface IEffortDataProviderResponse {
    data: IEffortData;
    errorMessage?: string;
    versionStamp?: string;
}

export interface IForecastSettings {
    effortData: IEffortData;
    velocity: number;
    visibleState: string;
    inProgressStates: string[];
}

export interface ITeam {
    id: string;
    name: string;
}

export interface IPageWorkItemsDataProviderResponse {
    data: IBacklogQueryResultsPayload;
    errorMessage?: string;
}
